import React, { useState, useEffect } from 'react';
import { FiX, FiFile, FiDownload, FiEye, FiCheck } from 'react-icons/fi';
import { MapContainer, TileLayer, Marker, Tooltip as LeafletTooltip } from 'react-leaflet';
import L from 'leaflet';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Zoom } from 'swiper/modules';
import 'leaflet/dist/leaflet.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/zoom';
import heic2any from 'heic2any';
import Api from '../../../Api';
import Modal from '../../../components/Modal';
import Button from '../../../components/Button';
import '../../../components/CompanyForm/style.css';
import './style.css';

// Fix Leaflet icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Utility functions
function formatCurrency(value) {
  if (!value) return '';
  if (String(value).includes('R$')) return value;
  
  let v = String(value).replace(/\D/g, '');
  v = (v / 100).toFixed(2) + '';
  v = v.replace('.', ',');
  v = v.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
  return `R$ ${v}`;
}

function formatPhone(value) {
  if (!value) return '';
  if (String(value).includes('(')) return value;

  let v = String(value).replace(/\D/g, '');
  if (v.length > 11) v = v.slice(0, 11);

  if (v.length <= 2) return `(${v}`;
  if (v.length <= 6) return `(${v.slice(0,2)}) ${v.slice(2)}`;
  if (v.length <= 10) return `(${v.slice(0,2)}) ${v.slice(2,6)}-${v.slice(6)}`;
  return `(${v.slice(0,2)}) ${v.slice(2,7)}-${v.slice(7)}`;
}

function formatDate(value) {
  if (!value) return '';
  try {
    const date = new Date(value);
    // Verifica se a data é válida
    if (isNaN(date.getTime())) return value;
    
    // Ajuste de fuso horário se necessário, mas new Date(string) geralmente trata bem ISO
    // Se a data vier YYYY-MM-DD, new Date() assume UTC e pode dar dia anterior dependendo do fuso
    // Vamos usar split para garantir se for YYYY-MM-DD
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-');
      return `${day}/${month}/${year}`;
    }
    
    return date.toLocaleDateString('pt-BR');
  } catch (e) {
    return value;
  }
}

function base64ToBlob(base64, type) {
  try {
    const parts = base64.split(';base64,');
    const contentType = parts[0].split(':')[1] || type;
    const raw = window.atob(parts[1]);
    const rawLength = raw.length;
    const uInt8Array = new Uint8Array(rawLength);

    for (let i = 0; i < rawLength; ++i) {
      uInt8Array[i] = raw.charCodeAt(i);
    }

    return new Blob([uInt8Array], { type: contentType });
  } catch (e) {
    console.error('Error converting base64 to blob', e);
    return null;
  }
}

function PreviewImage({ photo, onClick, className, style }) {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isActive = true;
    const rawData = photo.data || photo.content;
    const type = photo.type || '';
    
    // Check if it's HEIC
    if (type.includes('heic') || (typeof rawData === 'string' && rawData.startsWith('data:image/heic'))) {
      setLoading(true);
      // Need blob for conversion
      const blob = base64ToBlob(rawData, 'image/heic');
      if (blob) {
        heic2any({
          blob,
          toType: "image/jpeg",
          quality: 0.8
        })
        .then((conversionResult) => {
          if (isActive) {
            const resultBlob = Array.isArray(conversionResult) ? conversionResult[0] : conversionResult;
            const url = URL.createObjectURL(resultBlob);
            setSrc(url);
            setLoading(false);
          }
        })
        .catch((e) => {
          console.error("HEIC conversion failed", e);
          if (isActive) {
            setSrc(rawData); // Fallback
            setLoading(false);
          }
        });
      } else {
        if (isActive) {
          setSrc(rawData);
          setLoading(false);
        }
      }
    } else {
      if (isActive) {
        setSrc(rawData);
        setLoading(false);
      }
    }

    return () => {
      isActive = false;
      // Cleanup object URL if created (difficult to track exactly which one, but generally good practice to revoke if we kept ref)
      // For now, relying on browser cleanup or simple overwrite
    };
  }, [photo]);

  if (loading) {
    return (
      <div className={className} style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f3f4f6', color: '#6b7280', fontSize: '12px' }}>
        Convertendo...
      </div>
    );
  }

  return <img src={src} alt={photo.name} className={className} style={style} onClick={() => onClick && onClick({ ...photo, data: src })} />;
}

export default function ViewExecutionModal({ isOpen, onClose, execution, companyId }) {
  const config = execution?.config;
  const answers = execution?.answers || {};
  const [lightboxData, setLightboxData] = useState(null); // { images: [], index: 0 }
  const [employees, setEmployees] = useState({});
  const [stations, setStations] = useState({});
  const [vehicles, setVehicles] = useState({});
  const [machineries, setMachineries] = useState({});

  // Close lightbox on escape
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setLightboxData(null);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  useEffect(() => {
    if (!execution) return;

    if (execution.references) {
      setEmployees(prev => ({ ...prev, ...execution.references.employees }));
      setStations(prev => ({ ...prev, ...execution.references.stations }));
      setVehicles(prev => ({ ...prev, ...execution.references.vehicles }));
      setMachineries(prev => ({ ...prev, ...execution.references.machineries }));
      return;
    }

    const fetchAllData = async () => {
        try {
          // Fetch all companies to ensure we can resolve names across companies
          let companyIds = [];
          try {
             const companiesResponse = await Api.getCompanies();
             const allCompanies = Array.isArray(companiesResponse) ? companiesResponse : (companiesResponse.data || []);
             companyIds = allCompanies.map(c => c.id);
          } catch (err) {
             console.error("Error fetching companies:", err);
          }

          // Ensure the execution's company is included if not already
          if (execution.company_id && !companyIds.includes(execution.company_id)) {
            companyIds.push(execution.company_id);
          }

          // If no companies found, at least try with the execution's company
          if (companyIds.length === 0 && execution.company_id) {
             companyIds = [execution.company_id];
          }

          const hasEmployee = config?.sections?.some(s => s.questions.some(q => q.type === 'employee'));
        const hasStation = config?.sections?.some(s => s.questions.some(q => q.type === 'station'));
        const hasVehicle = config?.sections?.some(s => s.questions.some(q => q.type === 'vehicle'));
        const hasMachinery = config?.sections?.some(s => s.questions.some(q => q.type === 'machinery' || q.type === 'machinery_select'));

        if (hasEmployee) {
          const allEmployees = [];
          await Promise.all(companyIds.map(async (cid) => {
            try {
              const response = await Api.getEmployees(cid);
              const data = Array.isArray(response) ? response : (response.data || []);
              if (Array.isArray(data)) allEmployees.push(...data);
            } catch (e) { }
          }));
          
          const map = {};
          allEmployees.forEach(e => map[e.id] = e.name);
          setEmployees(map);
        }

        if (hasStation) {
          const allStations = [];
          await Promise.all(companyIds.map(async (cid) => {
            try {
              const response = await Api.getServiceStations(cid);
              const data = Array.isArray(response) ? response : (response.data || []);
              if (Array.isArray(data)) allStations.push(...data);
            } catch (e) { }
          }));

          const map = {};
          allStations.forEach(s => map[s.id] = s.name);
          setStations(map);
        }

        if (hasVehicle) {
          const allVehicles = [];
          await Promise.all(companyIds.map(async (cid) => {
            try {
              const response = await Api.getVehicles(cid);
              const data = Array.isArray(response) ? response : (response.data || []);
              if (Array.isArray(data)) allVehicles.push(...data);
            } catch (e) { }
          }));

          const map = {};
          allVehicles.forEach(v => {
            const id = v.id ?? v.code ?? v.vehicle_id ?? v.veiculo_id;
            const plate = v.plate || v.license_plate || v.placa || v.code || '';
            const name = v.name || v.description || v.descricao || v.modelo || v.model || '';
            const baseLabel = name || `Veículo ${id}`;
            const label = plate ? `${plate} - ${baseLabel}` : baseLabel;
            map[id] = label;
          });
          setVehicles(map);
        }

        if (hasMachinery) {
          const allMachineries = [];
          await Promise.all(companyIds.map(async (cid) => {
            try {
              const response = await Api.getMachineries(cid);
              const data = Array.isArray(response) ? response : (response.data || []);
              if (Array.isArray(data)) allMachineries.push(...data);
            } catch (e) { }
          }));

          const map = {};
          allMachineries.forEach(m => {
            const id = m.id;
            const tag = m.tag || '';
            const model = m.model || '';
            const brand = m.brand || '';
            let label = `${tag} - ${model}`;
            if (brand) label += ` (${brand})`;
            map[id] = label;
          });
          setMachineries(map);
        }

      } catch (error) {
        console.error("Error fetching reference data:", error);
      }
    };

    fetchAllData();
  }, [execution, config]);

  const questionMap = React.useMemo(() => {
    if (!config || !config.sections) return {};
    const map = {};
    config.sections.forEach(section => {
      section.questions.forEach(q => {
        map[q.id] = q;
      });
    });
    return map;
  }, [config]);

  if (!execution || !config) return null;

  function isQuestionVisible(question, visited = new Set()) {
    if (!question.conditional?.enabled) return true;
    
    const { referenceId, expectedValue } = question.conditional;
    if (!referenceId) return true;

    if (visited.has(question.id)) return true;
    visited.add(question.id);

    const refQuestion = questionMap[referenceId];
    if (refQuestion) {
      if (!isQuestionVisible(refQuestion, new Set(visited))) return false;
    }

    const refAnswer = answers[referenceId];
    
    // Strict comparison for non-array values
    if (refAnswer === undefined || refAnswer === null) {
       if (expectedValue === 'nao_contem') return true;
       return false;
    }

    // Handle "Contém" / "Não Contém" special logic
    if (expectedValue === 'contem') {
      if (Array.isArray(refAnswer)) return refAnswer.length > 0;
      if (typeof refAnswer === 'string') return refAnswer.trim().length > 0;
      return true;
    }

    if (expectedValue === 'nao_contem') {
      if (Array.isArray(refAnswer)) return refAnswer.length === 0;
      if (typeof refAnswer === 'string') return refAnswer.trim().length === 0;
      return false;
    }
    
    if (Array.isArray(refAnswer)) {
      return refAnswer.includes(expectedValue);
    }
    
    return String(refAnswer) === String(expectedValue);
  }

  function handleViewFile(file) {
    const blob = base64ToBlob(file.data || file.content, file.type);
    if (blob) {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Clean up URL after a delay (enough time for browser to open it)
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
  }

  function renderAnswer(question) {
    const rawValue = answers[question.id];
    const value = rawValue;

    if (value === undefined || value === null || value === '') {
      return <span className="text-gray-400 italic">Sem resposta</span>;
    }

    switch (question.type) {
      case 'photo':
        // value should be array of { name, type, data (base64) }
        if (!Array.isArray(value) || value.length === 0) return <span className="text-gray-400 italic">Sem fotos</span>;
        return (
          <div className="preview-grid">
            {value.map((photo, idx) => (
              <div key={idx} className="preview-item">
                <PreviewImage photo={photo} className="preview-image" />
                <div className="file-actions-overlay">
                  <button
                    type="button"
                    className="file-action-btn"
                    onClick={() => setLightboxData({ images: value, index: idx })}
                    title="Visualizar"
                  >
                    <FiEye size={16} />
                  </button>
                  <a 
                    href={photo.data || photo.content} 
                    download={photo.name}
                    className="file-action-btn"
                    title="Baixar"
                  >
                    <FiDownload size={16} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        );

      case 'document':
        if (!Array.isArray(value) || value.length === 0) return <span className="text-gray-400 italic">Sem documentos</span>;
        return (
          <div className="preview-grid">
            {value.map((doc, idx) => (
              <div key={idx} className="preview-item">
                <div className="preview-file">
                  <FiFile size={24} />
                  <span style={{ wordBreak: 'break-word' }}>{doc.name}</span>
                </div>
                <div className="file-actions-overlay">
                  <button
                    type="button"
                    className="file-action-btn"
                    onClick={() => handleViewFile(doc)}
                    title="Visualizar"
                  >
                    <FiEye size={16} />
                  </button>
                  <a 
                    href={doc.data || doc.content} 
                    download={doc.name}
                    className="file-action-btn"
                    title="Baixar"
                  >
                    <FiDownload size={16} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        );

      case 'signature':
        if (!value) return <span className="text-gray-400 italic">Não assinado</span>;
        return (
          <div className="signature-display">
            <img 
              src={value} 
              alt="Assinatura" 
              className="signature-image" 
              style={{ maxWidth: '100%', maxHeight: '150px', border: '1px solid #ddd', borderRadius: '4px' }} 
              onClick={() => setLightboxData({ images: [{ data: value, name: 'Assinatura' }], index: 0 })}
              title="Clique para ampliar"
            />
          </div>
        );

      case 'monetary':
        return <span>{formatCurrency(value)}</span>;

      case 'phone':
        return <span>{formatPhone(value)}</span>;

      case 'date':
        return <span>{formatDate(value)}</span>;

      case 'single_choice':
      case 'select':
        return (
          <div className="radio-group">
            {question.options?.map((option, idx) => {
              const isSelected =
                value === option.text ||
                String(value) === String(option.id) ||
                (option.value && String(value) === String(option.value));
              return (
                <div
                  key={idx}
                  className={`custom-radio ${isSelected ? 'selected' : ''}`}
                >
                  <div className="radio-indicator" />
                  <span>{option.text}</span>
                </div>
              );
            })}
          </div>
        );

      case 'multiple_choice': {
        const rawValues = Array.isArray(rawValue)
          ? rawValue
          : rawValue
          ? [rawValue]
          : [];
        const rawSet = new Set(rawValues.map(v => String(v)));
        const canonicalSelectedTexts =
          question.options
            ?.filter(opt =>
              rawSet.has(String(opt.text)) ||
              rawSet.has(String(opt.id)) ||
              (opt.value && rawSet.has(String(opt.value)))
            )
            .map(opt => opt.text) || [];

        if (canonicalSelectedTexts.length === 0) {
          return <span className="text-gray-400 italic">Sem resposta</span>;
        }

        return (
          <div className="checkbox-group">
            {question.options?.map((option, idx) => {
              const isSelected = canonicalSelectedTexts.includes(option.text);
              return (
                <div
                  key={idx}
                  className={`custom-checkbox ${isSelected ? 'selected' : ''}`}
                >
                  <div className="checkbox-indicator">
                    {isSelected && <FiCheck size={14} />}
                  </div>
                  <span>{option.text}</span>
                </div>
              );
            })}
          </div>
        );
      }

      case 'employee':
         const empLabel = employees[value] || employees[String(value)];
         return <span>{empLabel || value}</span>;

      case 'station':
         const stationLabel = stations[value] || stations[String(value)];
         return <span>{stationLabel || value}</span>;

      case 'vehicle':
         const vehicleLabel = vehicles[value] || vehicles[String(value)];
         return <span>{vehicleLabel || value}</span>;

      case 'machinery':
      case 'machinery_select':
         const machineryLabel = machineries[value] || machineries[String(value)];
         return <span>{machineryLabel || value}</span>;

      default:
        return <span>{String(value)}</span>;
    }
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Visualizar: ${execution.inspection_name}`}
        size="lg"
      >
        <div className="company-form-component view-mode">
          <div className="form-body">
            <div className="execution-info-header" style={{ flexWrap: 'wrap' }}>
              <div className="info-item">
                <strong>Executor</strong>
                <span>{execution.executor_name || 'N/A'}</span>
              </div>
              <div className="info-item">
                <strong>Data de Execução</strong>
                <span>{new Date(execution.executed_at).toLocaleString('pt-BR')}</span>
              </div>
              <div className="info-item">
                <strong>ID da Execução</strong>
                <span>#{execution.id}</span>
              </div>
              <div className="info-item">
                <strong>Modelo</strong>
                <span>#{config.id}</span>
              </div>

              {execution.latitude && execution.longitude && (
                <div style={{ width: '100%', height: '300px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0', zIndex: 1 }}>
                  <MapContainer 
                    center={[execution.latitude, execution.longitude]} 
                    zoom={15} 
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker position={[execution.latitude, execution.longitude]}>
                      <LeafletTooltip permanent>
                        {new Date(execution.executed_at).toLocaleString('pt-BR')}
                      </LeafletTooltip>
                    </Marker>
                  </MapContainer>
                </div>
              )}
            </div>
            {config.sections?.map(section => (
              <div key={section.id} className="execution-section">
                <div className="execution-section-header">
                  {section.title}
                </div>
                <div className="execution-section-body">
                  {section.questions.map(question => {
                    if (!isQuestionVisible(question)) return null;

                    return (
                      <div key={question.id} className="execution-question">
                        <label className="execution-question-label">
                          {question.text}
                        </label>
                        <div className="execution-answer-display">
                          {renderAnswer(question)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="modal-actions">
            <Button
              type="button"
              className="cancel-button"
              onClick={onClose}
            >
              Fechar
            </Button>
          </div>
        </div>
      </Modal>

      {lightboxData && (
        <div className="lightbox-overlay" onClick={() => setLightboxData(null)}>
          <button className="lightbox-close" onClick={() => setLightboxData(null)}>
            <FiX />
          </button>
          <div className="lightbox-content" onClick={e => e.stopPropagation()} style={{ width: '90vw', height: '90vh' }}>
             <Swiper
               modules={[Navigation, Pagination, Zoom]}
               spaceBetween={30}
               slidesPerView={1}
               navigation
               pagination={{ clickable: true }}
               zoom={true}
               initialSlide={lightboxData.index}
               style={{ width: '100%', height: '100%' }}
             >
               {lightboxData.images.map((img, idx) => (
                 <SwiperSlide key={idx}>
                   <div className="swiper-zoom-container">
                     <PreviewImage photo={img} className="lightbox-image" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                   </div>
                 </SwiperSlide>
               ))}
             </Swiper>
          </div>
        </div>
      )}
    </>
  );
}
